import { useEffect } from "react";
import { useForm } from "react-hook-form";
import { FormInput } from "../../shared/formElements/formInput/FormInput";
import { FormCheckboxChips } from "../../shared/formElements/formCheckBoxChip/FormCheckBoxChip";

import { FormCalendar } from "../../shared/formElements/formCalendar/FormCalendar";

import { FormRichText } from "../../shared/formElements/formRichText/FormRichtext";
import classes from "./couponDetailForm.module.css";
import { FormDropdown } from "../../shared/formElements/formDropdown/FormDropdown";

const categories = [
  { id: 5, label: "VRP" },
  { id: 6, label: "Spares" },
  { id: 8, label: "Open Box" },
  { id: 9, label: "Prexo" },
  { id: 7, label: "New Phones" },
];

const memberships = [
  { id: 1, label: "Silver" },
  { id: 2, label: "Gold" },
  { id: 3, label: "Platinum" },
];

const discountTypes = [
  { id: "percentage", label: "Percentage" },
  { id: "amount", label: "Amount" },
  { id: "delivery_waiver", label: "Delivery Waiver" },
];

export const CouponDetailForm = ({
  initialValues,
  onSubmit,
  onDelete,
  isSubmitting,
  isDeleting,
}) => {
  const {
    register,
    handleSubmit,
    reset,
    watch,
    setValue,
    formState: { isValid, errors },
  } = useForm({
    defaultValues: initialValues,
    mode: "onChange",
  });

  // Register ReactQuill manually for required validation
  useEffect(() => {
    reset(initialValues);

    // ✅ category multi-select
    if (Array.isArray(initialValues?.category_id)) {
      setValue("category_id", initialValues.category_id, {
        shouldValidate: true,
      });
    }
    if (Array.isArray(initialValues?.membership)) {
      setValue("membership", initialValues.membership, {
        shouldValidate: true,
      });
    }

    register("descrption", {
      required: "Coupon description is required",
      validate: (val) => {
        const text = new DOMParser()
          .parseFromString(val || "", "text/html")
          .body.textContent.trim();
        return text.length > 0 || "Coupon description is required";
      },
    });
  }, [initialValues, reset, register, setValue]);

  return (
    <form className={classes.form} onSubmit={handleSubmit(onSubmit)}>
      {/* -------------------- BASIC INFO -------------------- */}
      <div className={classes.section}>
        <h3 className={classes.sectionTitle}>Coupon Information</h3>

        <div className={classes.grid}>
          <FormInput
            id="code"
            label="Coupon Code"
            register={register}
            validation={{ required: "Coupon Code required" }}
            error={errors.code?.message}
            required
          />

          <FormCalendar
            id="start_date"
            label="Start Date"
            watch={watch}
            setValue={setValue}
            register={register}
            required
            error={errors.start_date?.message}
          />
          <FormCalendar
            id="end_date"
            label="End Date"
            watch={watch}
            setValue={setValue}
            register={register}
            required
            error={errors.end_date?.message}
          />
        </div>
      </div>

      {/* -------------------- DESCRIPTION -------------------- */}
      <div className={classes.section}>
        <FormRichText
          id="descrption"
          label="Coupon Description"
          value={watch("descrption")}
          onChange={(val) =>
            setValue("descrption", val, { shouldValidate: true })
          }
          required
          error={errors.descrption?.message}
        />
      </div>

      <div className={classes.section}>
        <h3 className={classes.sectionTitle}>Applicable On Category</h3>
        <FormCheckboxChips
          id="category_id"
          label="Categories"
          options={categories}
          watch={watch}
          setValue={setValue}
          register={register}
          validation={{ required: "Category is required" }}
          error={errors.category_id?.message}
          required
        />
      </div>

      <div className={classes.section}>
        <h3 className={classes.sectionTitle}>Applicable On Member Type</h3>
        <FormCheckboxChips
          id="membership"
          label="Membership Type"
          options={memberships}
          watch={watch}
          setValue={setValue}
          register={register}
          validation={{ required: "Membership Type is required" }}
          error={errors.membership?.message}
          required
        />
      </div>
      <div className={classes.section}>
        <h3 className={classes.sectionTitle}>Pricing Information</h3>

        <div className={classes.grid}>
          <FormDropdown
            id="discount_type"
            label="Discount Type"
            options={discountTypes}
            register={register}
            setValue={setValue}
            watch={watch}
            validation={{ required: "Discount Type is required" }}
            error={errors.discount_type?.message}
            required
          />
          <FormInput
            id="discount_value"
            label="Discount Value"
            register={register}
            validation={{ required: "Discount Value is required" }}
            error={errors.discount_value?.message}
            required
          />
          <FormInput
            id="min_order_value"
            label="Min Order Value"
            register={register}
            validation={{ required: "Min Order Value is required" }}
            error={errors.min_order_value?.message}
            required
          />
          <FormInput
            id="max_redeemable_amount"
            label="Max Redeemable Amount"
            register={register}
            validation={{ required: "Max Redeemable Amount is required" }}
            error={errors.max_redeemable_amount?.message}
            required
          />
        </div>
      </div>

      <div className={classes.actions}>
        <button
          type="button"
          className={classes.deleteBtn}
          onClick={onDelete}
          disabled={isDeleting}
        >
          {isDeleting ? "Deleting..." : "Delete"}
        </button>

        <button
          type="submit"
          className={classes.submitBtn}
          disabled={!isValid || isSubmitting}
        >
          {isSubmitting ? "Updating..." : "Update Coupon"}
        </button>
      </div>
    </form>
  );
};
